import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import styled from '@emotion/styled';
import { Checkbox, message } from 'antd';
import AppLoading from 'components/AppLoading';
import useSmartField from 'hooks/useSmartField';
import { fetchScenarioPackList } from 'services/api/storeGroup';
import { useTranslate } from '@tolgee/react';
import { useQaContext } from 'views/Aide/Qa/Context';
import { useNonPlatformQaEnable } from 'hooks/business/useOrgConfig';

export const Title = styled.title`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 16px;
  text-indent: 10px;
  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    top: 4px;
    left: 0;
    width: 5px;
    height: 16px;
    background: var(--ant-primary-color);
  }
  > h3 {
    margin-bottom: 0;
    font-size: 16px;
  }
  > span {
    font-size: 12px;
  }
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  margin-bottom: 20px;
`;

const Box = styled.div`
  position: relative;
  height: 67px;
  padding: 10px 14px;
  border: solid 1px #a4aeba;
  border-radius: 2px;
  > span {
    display: block;
    margin-top: 5px;
    text-indent: 23px;
    font-size: 12px;
    color: #515359;
  }
`;

const New = styled.div`
  position: absolute;
  top: -1px;
  right: -1px;
  width: 37px;
  height: 14px;
  line-height: 14px;
  font-size: 14px;
  text-align: center;
  color: #fff;
  background: #3370ff;
  border-radius: 2px;
`;

const All = styled.div`
  margin-bottom: 10px;
`;

export interface ScenarioPackPickerProps extends HTMLAttributes<HTMLDivElement> {
  storeGroupId?: number;
  domainList: string[];
}

function ScenarioPackPicker(props: ScenarioPackPickerProps) {
  const { storeGroupId, domainList, ...otherProps } = props;

  const { t } = useTranslate();

  const { platform, region } = useQaContext();

  const nonPlatformQaEnable = useNonPlatformQaEnable();

  const {
    field: { value, onChange },
  } = useSmartField<Store.ScenarioPackValue>('scenePackValue');

  const refs = useRef({
    onChange,
    t,
  });
  refs.current = {
    onChange,
    t,
  };

  const [loading, setLoading] = useState(false);

  const [initValue, setInitValue] = useState<Store.ScenarioPackValue>();

  const { initialPickedList, restList, scenePackIdIndexMap } = useMemo(() => {
    return (initValue ? initValue.scenePacks : []).reduce<{
      initialPickedList: Store.ScenarioPack[];
      restList: Store.ScenarioPack[];
      scenePackIdIndexMap: Record<number, number>;
    }>(
      (res, scenarioPack, index) => {
        const { initialPickedList, restList, scenePackIdIndexMap } = res;

        const { id, ableUnsubscribe, hasSubscribed } = scenarioPack;

        if (!ableUnsubscribe || hasSubscribed) {
          initialPickedList.push(scenarioPack);
        } else {
          restList.push(scenarioPack);
        }

        scenePackIdIndexMap[id] = index;

        return res;
      },
      {
        initialPickedList: [],
        restList: [],
        scenePackIdIndexMap: {},
      },
    );
  }, [initValue]);

  const isRestAllChecked = restList.every((pack) => {
    const { id } = pack;

    const { [id]: index } = scenePackIdIndexMap;

    const {
      scenePacks: { [index]: { hasSubscribed = false } = {} },
    } = value;

    return hasSubscribed;
  });

  const isRestAllIndeterminate =
    !isRestAllChecked &&
    restList.some((pack) => {
      const { id } = pack;

      const { [id]: index } = scenePackIdIndexMap;

      const {
        scenePacks: { [index]: { hasSubscribed = false } = {} },
      } = value;

      return hasSubscribed;
    });

  useEffect(() => {
    if (domainList.length === 0) {
      return;
    }

    setLoading(true);

    fetchScenarioPackList({
      id: storeGroupId,
      domains: domainList.join(','),
      region,
      externalType: nonPlatformQaEnable ? 'MIXED' : platform,
    })
      .then((scenarioPackValue) => {
        batchedUpdates(() => {
          const {
            current: { onChange },
          } = refs;

          onChange(scenarioPackValue);
          setInitValue(scenarioPackValue);
          setLoading(false);
        });
      })
      .catch(() => {
        setLoading(false);

        const {
          current: { t },
        } = refs;

        message.error(t('storeGroup.获取场景包失败，请稍后再试'));
      });
  }, [storeGroupId, domainList, region, nonPlatformQaEnable, platform]);

  const scenarioPackRender = (scenarioPack: Store.ScenarioPack) => {
    const { id } = scenarioPack;

    const { [id]: index } = scenePackIdIndexMap;

    const {
      scenePacks: { [index]: pack },
    } = value;

    if (pack === undefined) {
      return null;
    }

    return <SubscribeCheckbox key={id} index={index} />;
  };

  if (loading) {
    return <AppLoading />;
  }

  return (
    <div {...otherProps}>
      {initialPickedList.length > 0 && (
        <>
          <Title>
            <h3>{t('storeGroup.已选场景包')}</h3>
            <span>{t('storeGroup.系统按照您选择的店铺为您准备的分类场景包')}</span>
          </Title>
          <List>{initialPickedList.map(scenarioPackRender)}</List>
        </>
      )}
      {restList.length > 0 && (
        <>
          <Title>
            <h3>{t('storeGroup.推荐场景包')}</h3>
          </Title>
          <All>
            <Checkbox
              checked={isRestAllChecked}
              indeterminate={isRestAllIndeterminate}
              onChange={({ target: { checked } }) => {
                const { mainVersionGroupId, scenePacks } = value;

                const nextScenePacks = [...scenePacks];

                restList.forEach((pack) => {
                  const { id } = pack;

                  const { [id]: index } = scenePackIdIndexMap;

                  nextScenePacks[index] = {
                    ...nextScenePacks[index],
                    hasSubscribed: checked,
                  };
                });

                onChange({
                  scenePacks: nextScenePacks,
                  mainVersionGroupId,
                });
              }}
            >
              {t('storeGroup.全选')}
            </Checkbox>
          </All>
          <List>{restList.map(scenarioPackRender)}</List>
        </>
      )}
    </div>
  );
}

interface SubscribeCheckboxProps extends HTMLAttributes<HTMLDivElement> {
  index: number;
}

function SubscribeCheckbox(props: SubscribeCheckboxProps) {
  const { index, ...otherProps } = props;

  const { t } = useTranslate();

  const {
    field: { value, onChange },
  } = useSmartField<Store.ScenarioPack>(`scenePackValue.scenePacks.${index}`);

  const { packName, sceneCount, ableUnsubscribe, hasSubscribed, isNew } = value;

  return (
    <Box {...otherProps}>
      <Checkbox
        disabled={!ableUnsubscribe}
        checked={hasSubscribed}
        onChange={({ target: { checked } }) => {
          onChange({
            ...value,
            hasSubscribed: checked,
          });
        }}
      >
        <span title={packName}>{packName}</span>
      </Checkbox>
      <span>
        {t('storeGroup.场景数量')}：{sceneCount}
      </span>
      {isNew && <New>NEW</New>}
    </Box>
  );
}

export default ScenarioPackPicker;
